import { AxiosResponse } from 'axios'
import { useEffect } from 'react'
import { isVisitTypeSelectableForHotel } from '@components/VisitTypeCategoriesAccordion/helpers'
import { getDestinations } from '../api'
import { getBookingConfigDestinationQueryParameter, getLang } from '../helpers'
import { useBookingFormStore } from '../providers/BookingFormStoreProvider'
import { handleApiErrorAndShowNotification } from '../store/bookingFormStore'
import { Identificator, IDestination, ILanguage, IVisitCategory } from '../store/data'
import { useDestinationsAccordion } from './useDestinationsAccordion'
import { useGuestsAccordion } from './useGuestsAccordion'
import { useHotelAccordion } from './useHotelAccordion'
import { DESTINATION_PARAM, GUEST_PARAM, useQueryParams } from './useQueryParams'
import { useRoomTypeAccordion } from './useRoomTypeAccordion'
import { useVisitTypeCategoriesAccordion } from './useVisitTypeCategoriesAccordion'

export const useLoadBookingForm = () => {
  const { addQueryParameter, removeQueryParameter, routerQueryParams } = useQueryParams()

  const { onInitGuestValidation } = useGuestsAccordion()
  const { onInitDestinationValidation } = useDestinationsAccordion()
  const { onInitHotelValidation } = useHotelAccordion()
  const { clearVisitTypeSelection, onInitVisitTypeValidation } = useVisitTypeCategoriesAccordion()
  const { onInitRoomTypeValidation } = useRoomTypeAccordion()

  const language = useBookingFormStore((state) => state.language)
  const getBookingConfiguration = useBookingFormStore((state) => state.getBookingConfiguration)
  const setDestinations = useBookingFormStore((state) => state.setDestinations)
  const setAppInitialized = useBookingFormStore((state) => state.setAppInitialized)
  const appInitialized = useBookingFormStore((state) => state.appInitialized)
  const setAppInitializedAndValidated = useBookingFormStore(
    (state) => state.setAppInitializedAndValidated
  )
  const appInitializedAndValidated = useBookingFormStore(
    (state) => state.appInitializedAndValidated
  )
  const setIsFetching = useBookingFormStore((state) => state.setIsFetching)
  const addNotification = useBookingFormStore((state) => state.addNotification)
  const validationState = useBookingFormStore((state) => state.validationState)
  const changeValidationState = useBookingFormStore((state) => state.changeValidationState)
  const roomType = useBookingFormStore((state) => state.roomType)
  const guest = useBookingFormStore((state) => state.guest)
  const hotel = useBookingFormStore((state) => state.hotel)
  const destination = useBookingFormStore((state) => state.destination)
  const destinations = useBookingFormStore((state) => state.destinations)
  const visitType = useBookingFormStore((state) => state.visitType)
  const visitTypeCategory = useBookingFormStore((state) => state.visitTypeCategory)
  const changeVisitType = useBookingFormStore((state) => state.changeVisitType)
  const changeVisitTypeCategory = useBookingFormStore((state) => state.changeVisitTypeCategory)
  const loadHotels = useBookingFormStore((state) => state.loadHotels)
  const loadVisitCategories = useBookingFormStore((state) => state.loadVisitCategories)
  const selectedHotelId = hotel?.id || null
  const selectedVisitTypeId = visitType?.id || null

  // init language and check booking config while app initialization
  useEffect(() => {
    const fetchInitialBookingConfiguration = async (initialLanguage: ILanguage) => {
      try {
        setIsFetching(true)

        let destinationsData = []
        let destinationsStatus = 200

        if (destinations.length) {
          destinationsData = destinations
        } else {
          const destinationsResponse: AxiosResponse<IDestination[]> = await getDestinations()
          destinationsData = destinationsResponse.data
          destinationsStatus = destinationsResponse.status
        }

        if (destinationsStatus === 200) {
          setDestinations(destinationsData)

          const bookingConfigDestinationName = getBookingConfigDestinationQueryParameter(
            routerQueryParams?.destination || null,
            initialLanguage,
            destinationsData
          )

          const bookingConfiguration = await getBookingConfiguration(
            {
              ...routerQueryParams,
              destination: bookingConfigDestinationName,
              lang: initialLanguage,
            },
            // Guests can be set during app initialization even when the guest parameter is empty, but beds are filled.
            async (guest: number | null, destination: IDestination | null) => {
              if (guest) {
                await addQueryParameter(GUEST_PARAM, `${guest}`)
              }
              if (destination) {
                const destinationName = getLang(destination.name, language)
                await addQueryParameter(DESTINATION_PARAM, destinationName)
              }
            }
          )

          if (!bookingConfiguration?.hotel) {
            await removeQueryParameter('hotel')
          }
        }
      } catch (error: any) {
        handleApiErrorAndShowNotification(error, addNotification)
      } finally {
        setIsFetching(false)
      }
    }

    if (!appInitialized) {
      const initializeBookingForm = async () => {
        await fetchInitialBookingConfiguration(language as ILanguage)
        setAppInitialized(true)
      }

      initializeBookingForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, appInitialized])

  useEffect(() => {
    const runValidationStep = async () => {
      if (!(appInitialized && !appInitializedAndValidated)) {
        return
      }

      switch (validationState) {
        case 'DESTINATIONS': {
          await onInitDestinationValidation(destination)
          changeValidationState('GUESTS')
          break
        }
        case 'GUESTS': {
          await onInitGuestValidation(guest)
          changeValidationState('HOTEL')
          break
        }
        case 'HOTEL': {
          await onInitHotelValidation(hotel)
          changeValidationState('VISIT_TYPE')
          break
        }
        case 'VISIT_TYPE': {
          await onInitVisitTypeValidation(visitType)
          changeValidationState('ROOM_TYPE')
          break
        }
        case 'ROOM_TYPE': {
          await onInitRoomTypeValidation(roomType)
          changeValidationState(null)
          break
        }
        case null: {
          setAppInitializedAndValidated(true)
          break
        }
        default:
      }
    }

    runValidationStep()
  }, [
    appInitialized,
    appInitializedAndValidated,
    setAppInitializedAndValidated,
    validationState,
    destination,
    guest,
    visitType,
    hotel,
    roomType,
  ])

  useEffect(() => {
    const syncSelectedVisitType = async (nextVisitCategories: IVisitCategory[]) => {
      if (!selectedVisitTypeId) {
        return
      }

      const matchingVisitTypeCategory = nextVisitCategories.find((category) =>
        category.visitTypes.some((item) => Number(item.id) === Number(selectedVisitTypeId))
      )
      const matchingVisitType =
        matchingVisitTypeCategory?.visitTypes.find(
          (item) => Number(item.id) === Number(selectedVisitTypeId)
        ) || null

      if (!matchingVisitTypeCategory || !matchingVisitType) {
        await clearVisitTypeSelection()
        return
      }

      if (selectedHotelId && !isVisitTypeSelectableForHotel(matchingVisitType, selectedHotelId, language)) {
        await clearVisitTypeSelection()
        return
      }

      if (visitType?.id !== matchingVisitType.id) {
        changeVisitType(matchingVisitType)
      }

      if (visitTypeCategory?.id !== matchingVisitTypeCategory.id) {
        changeVisitTypeCategory(matchingVisitTypeCategory)
      }
    }

    const fetchVisitCategories = async (destinationId: Identificator, guestCount: number | null) => {
      const nextVisitCategories = await loadVisitCategories({
        destination_id: destinationId,
        number_of_beds: guestCount,
      })

      await syncSelectedVisitType(nextVisitCategories)
    }

    if (appInitialized && appInitializedAndValidated && destination?.id) {
      fetchVisitCategories(destination.id, guest)
    }
  }, [
    appInitialized,
    appInitializedAndValidated,
    changeVisitType,
    changeVisitTypeCategory,
    clearVisitTypeSelection,
    destination?.id,
    guest,
    language,
    loadVisitCategories,
    selectedHotelId,
    selectedVisitTypeId,
    visitTypeCategory?.id,
  ])

  useEffect(() => {
    const fetchHotels = async (destinationId: Identificator) => {
      await loadHotels({
        destination_id: destinationId,
        include_room_types: false,
        number_of_beds: guest,
        visit_type_id: visitType?.id || null,
      })
    }

    // Only load hotels when no hotel is selected AND validation is complete
    // This prevents reloading hotels when a hotel is already selected
    const shouldLoadHotels = !selectedHotelId

    if (appInitialized && appInitializedAndValidated && destination?.id && shouldLoadHotels) {
      fetchHotels(destination.id)
    }
  }, [loadHotels, appInitialized, appInitializedAndValidated, destination?.id, guest, selectedVisitTypeId, selectedHotelId])
}
